import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { useTourProgress, useIntroData, useMaterializeIntroData, type SeenState } from 'hooks/api'
import { useCanAccess } from 'hooks'
// Direct subpath rather than the `components` barrel: this provider wraps the entire
// app, and pulling the barrel in here would drag every component into its module graph.
import { ConfirmSettingModal } from 'components/feedback/modals/ConfirmSettingModal'
import type { TourProgress } from 'services/tourProgressApi'
// Direct subpath, matching the `services/tourProgressApi` import above rather than
// pulling the `services` barrel into a provider that wraps the whole app.
import { tourAnalyticsApi } from 'services/tourAnalyticsApi'

import { ProductTour, type TourEndReason, type TourEvent } from './ProductTour'
import { getTour, type TourStep } from './tours'

interface ProductTourContextValue {
  /** Start a tour by id. Always starts, even if it has been seen — used by the
   *  Help menu, where starting a tour is an explicit request. */
  startTour: (id: string) => void
  /** Start a tour at most once per USER, and only when no tour is already
   *  running. For contextual auto-triggers (e.g. a surface a user just reached
   *  for the first time).
   *
   *  Safe to call before progress has loaded: the request is QUEUED and
   *  re-evaluated once the answer arrives. A caller firing this from a mount
   *  effect on a cold load — which is the only time it matters — would otherwise
   *  be dropped silently. */
  startTourOnce: (id: string) => void
  /** Mark a tour as seen without running it. */
  markTourSeen: (id: string) => void
  /**
   * `undefined` means NOT YET KNOWN, and is a genuinely different answer from
   * `false` ("known not to have been seen"). Treating unknown as unseen is what
   * makes a completed tour flash on every page load while the fetch is in
   * flight — the exact regression this feature exists to avoid. Gate on
   * `isTourProgressReady`, or just use `startTourOnce`, which does it for you.
   */
  hasSeenTour: (id: string) => SeenState
  /** True once `hasSeenTour` can return a real answer. */
  isTourProgressReady: boolean
  /** Recorded progress for a tour: `'completed'` only when the user reached the end,
   *  `'dismissed'` for skipping or dismissing the invitation, `'in_progress'` with a
   *  `currentStep` for one left part-way. `undefined` = untaken or not yet known. */
  tourProgress: (id: string) => TourProgress | undefined
  activeTourId: string | null
}

// The default is a working no-op so a consumer rendered outside the provider
// (an auth page, a test, a Storybook story) never crashes. `hasSeenTour` returns
// `undefined` — outside a provider there is genuinely no answer, and claiming
// `false` would license an auto-start on a surface that has no tours at all.
const ProductTourContext = createContext<ProductTourContextValue>({
  startTour: () => {},
  startTourOnce: () => {},
  markTourSeen: () => {},
  hasSeenTour: () => undefined,
  isTourProgressReady: false,
  tourProgress: () => undefined,
  activeTourId: null,
})

export const useProductTour = (): ProductTourContextValue => useContext(ProductTourContext)

/**
 * Custom event dispatched when a step's `onEnter`, `awaitAction.action` or
 * `cta.action` fires. Surfaces listen for it and perform the action, which keeps
 * the registry pure data — a tour never imports a page's internals.
 *
 *   useEffect(() => {
 *     const onTourAction = (e: Event) => {
 *       if ((e as CustomEvent<TourActionDetail>).detail.action === 'open-create-modal') setOpen(true)
 *     }
 *     window.addEventListener(TOUR_ACTION_EVENT, onTourAction)
 *     return () => window.removeEventListener(TOUR_ACTION_EVENT, onTourAction)
 *   }, [])
 */
export const TOUR_ACTION_EVENT = 'vera:product-tour-action'

export interface TourActionDetail {
  action: string
}

// ─── Persistence ───────────────────────────────────────────────────────────
// Progress is now per-USER and server-backed (VRG-228 / E4), so a tour finished
// on a laptop does not replay on a phone or in a fresh browser, and completion
// is reportable. The policy — tri-state unknown/seen/unseen, one-time migration
// of the old per-browser localStorage flags, and localStorage as the fallback
// when the API is unreachable — lives in `hooks/api/useTourProgress`, which this
// provider drives. The seam is unchanged: everything user-visible still goes
// through hasSeen/markSeen, they are just no longer module-scope functions,
// because they now depend on fetched state.

/**
 * Hosts the running tour and owns the "which tour is active" state.
 *
 * Mounted inside `Layout`, so it is only ever present on the chromed app shell —
 * a tour overlay on the sign-in page or the password-setup gate is a category
 * error, and scoping it to Layout makes that structurally impossible rather than
 * a path list to maintain.
 *
 * Deep link: `?tour=<id>` on any in-app route force-starts that tour and then
 * strips the param, so a refresh doesn't re-fire it. That is what makes a tour
 * linkable from an email, a doc, or the tour list on a different route.
 */
export const ProductTourProvider = ({ children }: { children: ReactNode }) => {
  const [activeTourId, setActiveTourId] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const { hasSeen, isReady, markSeen, markStep, progressOf } = useTourProgress()

  // VRG-512 — a tour whose launch is waiting on the user's answer to "add sample data
  // first?". Null when nothing is pending.
  const [seedPromptTourId, setSeedPromptTourId] = useState<string | null>(null)
  const { hasIntroData } = useIntroData()
  const { materializeAsync, isMaterializing } = useMaterializeIntroData()
  // VRG-512 — `POST /intro-data` is gated on `sample_data_manage`, so without it the
  // seed prompt below would offer an action the server refuses.
  const { isHasAccess: canManageSampleData } = useCanAccess({ action: 'sample_data_manage' })

  /**
   * Start a tour — NAVIGATING TO ITS SURFACE FIRST when the user is somewhere else.
   *
   * Tour steps resolve by `data-tour` anchor, and those anchors only exist on the page the
   * tour is about. Before this, `startTour` just set the active id wherever you happened to
   * be, so launching `roles-and-permissions` from the tour list on Home searched for anchors
   * that live on /settings/roles, found none, and attached to nothing. Nearly every tour in
   * the dialog was reachable that way, so nearly every one was broken from it.
   *
   * The navigation reuses the EXISTING `?tour=<id>` deep link rather than adding a second
   * start path: the effect below runs on every param change precisely so an in-app navigation
   * can carry it, and it strips the param immediately. The tour therefore begins after the
   * destination has rendered, which is the whole point.
   *
   * No redirect loop: by the time that effect fires we are already on `tour.route`, so the
   * pathname check below is false and this falls through to starting normally.
   *
   * A tour with no `route` is record-scoped (a specific certificate, a specific role) and has
   * no static destination — it starts in place, which is correct because the list only offers
   * it while you are on the record.
   */
  /**
   * VRG-512 — a tour that spotlights records, launched into an empty console, offers
   * to add sample data first. This is the SECOND entry point to intro data; Home's CTA
   * is the first.
   *
   * ── Why the gate sits HERE and not at the top of `startTour` ────────────────────
   * `startTour` is re-entered for any tour with a `route` launched from elsewhere:
   * the first call navigates to `?tour=<id>` and returns, then the deep-link effect
   * calls `startTour` again at the destination. A confirm at the top of the function
   * would therefore open TWICE for exactly the cross-route launches that are most
   * common from the tours dialog. Gating immediately before `setActiveTourId` — the
   * one line both passes converge on — fires it once.
   *
   * ── Why auto-start is deliberately NOT gated ────────────────────────────────────
   * `tryStartOnce` reaches `setActiveTourId` directly and is left alone. It fires
   * without the user having clicked anything, so a seeding confirm there would be an
   * unprompted modal proposing an unprompted write — precisely the invariant intro
   * data is built around (never write without an explicit user action). An auto-start
   * tour simply runs against whatever the console holds; the engine already degrades
   * gracefully on missing anchors.
   *
   * ── Unknown falls through, and that is a considered trade ───────────────────────
   * The gate asks for `hasIntroData === false` — KNOWN absent — not `!== true`. During
   * the sub-second window before the status query settles we start the tour rather
   * than offering to seed. Offering would risk proposing sample data to a tenant that
   * already has it, which is a worse and more confusing wrong than a tour that starts
   * with one fewer record to point at.
   */
  const requestTourSeed = useCallback(
    (id: string) => {
      const tour = getTour(id)
      if (!tour?.requiresIntroData) return false
      if (introStatusRef.current !== false) return false
      /*
        VRG-512 — SKIP the prompt entirely without `sample_data_manage`, rather than
        raising it and disabling its confirm.

        §46's disable-with-a-tooltip rule governs a control sitting on a surface the
        user is already looking at. This is not that: the whole modal IS the offer, so
        there is no surrounding context for a disabled button to explain. Raising an
        unprompted modal whose only action is dead, to interrupt a tour the user did
        ask for, is strictly worse than not raising it.

        Falling through here lands on exactly `handleSeedDecline`'s outcome — the tour
        starts against whatever the console holds — which is already the documented
        behaviour for a user who says no.
      */
      if (!canManageSampleDataRef.current) return false
      setSeedPromptTourId(id)
      return true
    },
    []
  )

  const startTour = useCallback(
    (id: string) => {
      const tour = getTour(id)
      if (!tour) return
      if (tour.route && pathname !== tour.route) {
        navigate(`${tour.route}?tour=${id}`)
        return
      }
      // Last thing before activation, so a cross-route launch cannot double-prompt.
      if (requestTourSeed(id)) return
      setActiveTourId(id)
    },
    [navigate, pathname, requestTourSeed]
  )

  // VRG-512. Mirrored into a ref for the same reason as `activeRef` below: the gate
  // runs inside `startTour`, whose identity must stay stable for the callers that
  // invoke it from effects. Taking `hasIntroData` as a dependency would churn it every
  // time the status query settles.
  const introStatusRef = useRef<boolean | undefined>(undefined)
  useEffect(() => {
    introStatusRef.current = hasIntroData
  }, [hasIntroData])

  // VRG-512 — mirrored for the same reason as `introStatusRef` above: `requestTourSeed`
  // is read from `startTour`, whose identity must stay stable for the effect-based
  // callers, so the permission cannot be a dependency.
  const canManageSampleDataRef = useRef(false)
  useEffect(() => {
    canManageSampleDataRef.current = canManageSampleData
  }, [canManageSampleData])

  // Mirror the active id into a ref so startTourOnce can read it without taking
  // activeTourId as a dependency — callers use it inside effects, so its
  // identity has to stay stable.
  const activeRef = useRef<string | null>(null)
  useEffect(() => {
    activeRef.current = activeTourId
  }, [activeTourId])

  // Keep the latest progress accessors in a ref for the same reason: they change
  // identity as the query settles, and startTourOnce must not.
  const progressRef = useRef({ hasSeen, markSeen })
  useEffect(() => {
    progressRef.current = { hasSeen, markSeen }
  }, [hasSeen, markSeen])

  /**
   * A tour id a caller asked to auto-start BEFORE progress was known.
   *
   * Without this, `startTourOnce` called from a mount effect on a cold load —
   * which is precisely when a first-visit tour should fire — would hit the
   * "don't know yet" branch and be dropped forever, since nothing calls it
   * again. Queue the request, then settle it once the answer arrives.
   */
  const pendingOnceRef = useRef<string | null>(null)

  const tryStartOnce = useCallback((id: string) => {
    if (!getTour(id) || activeRef.current) return
    const seen = progressRef.current.hasSeen(id)
    // UNKNOWN, not unseen. Park it and re-evaluate when progress resolves; do
    // NOT fall through and start, which is the flash-on-every-load bug.
    if (seen === undefined) {
      pendingOnceRef.current = id
      return
    }
    pendingOnceRef.current = null
    if (seen) return
    // Mark before showing so a re-render can't double-fire it. The mark is
    // optimistic locally, so the second call in the same tick already sees it.
    progressRef.current.markSeen(id, 'dismissed')
    setActiveTourId(id)
  }, [])

  const startTourOnce = useCallback(
    (id: string) => {
      tryStartOnce(id)
    },
    [tryStartOnce]
  )

  // Settle a queued auto-start once progress becomes knowable — from the server,
  // or from the localStorage fallback after a failed fetch.
  useEffect(() => {
    if (!isReady) return
    const pending = pendingOnceRef.current
    if (pending) tryStartOnce(pending)
  }, [isReady, tryStartOnce])

  // Deep-link start. Runs on every param change (not mount-once) so navigating
  // to `?tour=x` from inside the app works, not just a cold load.
  useEffect(() => {
    const requested = searchParams.get('tour')
    if (!requested) return
    // Strip the param first, so an unknown id doesn't stick around either.
    const next = new URLSearchParams(searchParams)
    next.delete('tour')
    setSearchParams(next, { replace: true })
    if (getTour(requested)) startTour(requested)
  }, [searchParams, setSearchParams, startTour])

  /**
   * Three end reasons, three outcomes — and only two of them are terminal.
   *
   *  - `complete` → `completed`. The only status that is evidence the user read it,
   *    which is why it is not collapsed with the others: doing so would make "how
   *    many people finished this tour?" unanswerable.
   *  - `skip`     → `dismissed`. An explicit decline; terminal, never re-offered.
   *  - `exit`     → NOTHING WRITTEN. Escape and the close button mean "not now", so
   *    the row is left `in_progress` at the step `markStep` already recorded. That
   *    is what lets the invitation come back as "Finish tour" and resume there.
   *
   * The `exit` branch writing nothing is the entire mechanism, and it works only
   * because `in_progress` is not terminal (`isTerminalTourStatus`) — so `hasSeen`
   * keeps returning false and the chip keeps offering it. Before this split every
   * non-completion wrote `dismissed`, which is why no tour was ever observably
   * `in_progress` and the "abandoned halfway has not seen it" comment in
   * `tourProgressApi` described something unreachable.
   */
  const handleEnd = useCallback(
    (reason: TourEndReason) => {
      if (activeTourId && reason !== 'exit') {
        markSeen(activeTourId, reason === 'complete' ? 'completed' : 'dismissed')
      }
      setActiveTourId(null)
    },
    [activeTourId, markSeen]
  )

  const dispatchAction = useCallback((action: string) => {
    window.dispatchEvent(
      new CustomEvent<TourActionDetail>(TOUR_ACTION_EVENT, { detail: { action } })
    )
  }, [])

  const handleCta = useCallback(
    (step: TourStep) => {
      if (step.cta?.action) dispatchAction(step.cta.action)
      // In-app route, so use the router rather than a full page load — a
      // location.assign here would tear down the SPA and lose all query state.
      else if (step.cta?.href) navigate(step.cta.href)
    },
    [dispatchAction, navigate]
  )

  const handleStepEnter = useCallback(
    (step: TourStep) => {
      if (step.onEnter) dispatchAction(step.onEnter)
    },
    [dispatchAction]
  )

  const handleAwaitAction = useCallback(
    (step: TourStep) => {
      if (step.awaitAction?.action) dispatchAction(step.awaitAction.action)
    },
    [dispatchAction]
  )

  // Branch from a hub step into another tour: mark the current one seen so it
  // won't auto-fire again, then switch. Branching away is leaving early, so the
  // hub is `dismissed`, not `completed`.
  const handleBranch = useCallback(
    (tourId: string) => {
      setActiveTourId(current => {
        if (current) markSeen(current, 'dismissed')
        return getTour(tourId) ? tourId : null
      })
    },
    [markSeen]
  )

  /**
   * The tour's one event sink — and it feeds TWO consumers, which is the whole
   * reason this function is worth reading.
   *
   *  1. **Progress** (`markStep`) — CURRENT STATE. One upserted row per (user,
   *     tour) so a half-finished tour resumes where it stopped. Only `start` and
   *     `step` write here; terminal transitions belong to `handleEnd`, and doing
   *     them in both places would race.
   *  2. **Analytics** (`tourAnalyticsApi.record`) — HISTORY. Every event type,
   *     appended and never updated, because a funnel is a question about what
   *     happened rather than about where anyone is now. This is what closed
   *     VRG-267's `TODO(VRG-226)`: the events used to be dispatched as a window
   *     CustomEvent that nothing listened to, and were then dropped.
   *
   * The analytics write is FIRE AND FORGET and cannot reject (`tourAnalyticsApi`
   * swallows every failure). Not laziness — the sink measuring a tour must not be
   * able to interrupt the tour, and there is no user-visible recovery from "your
   * step-3 event did not record" worth interrupting anyone for.
   */
  const handleEvent = useCallback(
    (event: TourEvent) => {
      if (event.type === 'step' || event.type === 'start') {
        markStep(event.tour, event.index)
      }
      void tourAnalyticsApi.record({
        tourKey: event.tour,
        stepId: event.step,
        stepIndex: event.index,
        // `event.total` is already the CONTENT count — the engine computes it as
        // `tour.steps.length`, excluding the outro it appends (§48). Recomputing
        // it here from `playableSteps` would put the funnel one step out of step
        // with the counter the user was reading.
        totalSteps: event.total,
        type: event.type,
      })
    },
    [markStep]
  )

  // `markTourSeen` defaults to `dismissed`: marking a tour seen WITHOUT running it
  // is by definition not a completion.
  const markTourSeen = useCallback((id: string) => markSeen(id, 'dismissed'), [markSeen])

  // Read once per active tour, at render — the row is already in the query cache,
  // and the engine clamps whatever it is given.
  const resumeProgress = activeTourId ? progressOf(activeTourId) : undefined

  const value = useMemo<ProductTourContextValue>(
    () => ({
      startTour,
      startTourOnce,
      markTourSeen,
      hasSeenTour: hasSeen,
      isTourProgressReady: isReady,
      tourProgress: progressOf,
      activeTourId,
    }),
    // hasSeen / markTourSeen change identity as the query settles — they MUST be
    // dependencies, or a consumer keeps calling a closure that still answers
    // "unknown" long after progress has loaded.
    [startTour, startTourOnce, markTourSeen, hasSeen, isReady, progressOf, activeTourId]
  )

  const active = activeTourId ? getTour(activeTourId) : undefined

  /**
   * VRG-512 — the user said yes to sample data, so seed and then run the tour.
   *
   * The seed is idempotent server-side, so a double-click or a retry cannot produce
   * two sets. On failure the tour still starts: a tour that walks a slightly emptier
   * console is a far better outcome than swallowing the user's explicit request to
   * take the tour because a seeding call failed.
   */
  const handleSeedConfirm = async () => {
    const id = seedPromptTourId
    if (!id) return
    try {
      await materializeAsync('tour-start')
    } catch {
      // Deliberately swallowed — see above. The tour is what they asked for.
    }
    setSeedPromptTourId(null)
    setActiveTourId(id)
  }

  /** Declined the samples. Run the tour anyway — they asked for the tour, not the data. */
  const handleSeedDecline = () => {
    const id = seedPromptTourId
    setSeedPromptTourId(null)
    if (id) setActiveTourId(id)
  }

  return (
    <ProductTourContext.Provider value={value}>
      {children}
      <ConfirmSettingModal
        open={Boolean(seedPromptTourId)}
        // Closing IS declining — dismissing a prompt you did not ask for should not
        // also cancel the tour you did ask for.
        onClose={handleSeedDecline}
        onConfirm={handleSeedConfirm}
        loading={isMaterializing}
        title="Add sample data first?"
        consequence={
          'This tour points at products and certificates, and your console is empty. ' +
          'We can add a few samples so there is something to look at. They are clearly ' +
          'labelled as samples, are never billed, and you can clear them in one click ' +
          'from Analytics whenever you like.'
        }
        confirmLabel="Add sample data"
      />
      {active && (
        <ProductTour
          tour={active}
          open
          onEnd={handleEnd}
          onEvent={handleEvent}
          onCta={handleCta}
          onStepEnter={handleStepEnter}
          onAwaitAction={handleAwaitAction}
          onBranch={handleBranch}
          // Resume where they stopped. `completed` is excluded so REPLAYING a
          // finished tour starts at the beginning — replaying from the last step
          // would show one card and end, which is not what "watch it again" means.
          startAtStep={
            resumeProgress && resumeProgress.status !== 'completed'
              ? resumeProgress.currentStep
              : 0
          }
        />
      )}
    </ProductTourContext.Provider>
  )
}
